/**
 * 처방 판정 테스트 패널 — 프리셋 로더·형식 검사(설계 결정 4, AC-63·66).
 *
 * JSON 원천은 ymyd-spec 합성기 산출물 `../presets/dose-review-presets.json`(내용 불변).
 * 로더는 형식을 검사해 어긋난 항목을 걸러낸다:
 * - 최상위 weightKg 금지(체중은 patient 안에 있어야 함 — 최상위로 보내면 조용히 무시됨)
 * - rows 길이 = request.prescriptions 길이(DrugSearchInput "선택됨" 판정용 itemCode 매핑)
 * - expect는 설계 결정 4의 10종
 */
import type { DoseReviewRequest } from "api/doseReviewService";
import presetFileJson from "../presets/dose-review-presets.json";

export type PresetExpect =
  | "pendingDowngrade"
  | "caution"
  // 미달(status underdose) — 카드/프리셋 표시 라벨은 "1회 부족(참고)"이다
  // (@spec:2026-09-18-처방검토-미달경고-참고등급 D-5). expect 갈래명은 생성 시점 기준이라 그대로 둔다.
  | "warning"
  | "info"
  | "insurance_code_not_registered"
  | "not_eligible"
  | "blocked"
  | "duplicate_ingredient"
  | "code_missing"
  | "normal";

const ALLOWED_EXPECT: ReadonlySet<string> = new Set<PresetExpect>([
  "pendingDowngrade",
  "caution",
  "warning",
  "info",
  "insurance_code_not_registered",
  "not_eligible",
  "blocked",
  "duplicate_ingredient",
  "code_missing",
  "normal",
]);

export interface Preset {
  id: string;
  name: string;
  expect: PresetExpect;
  request: DoseReviewRequest;
  rows: Array<{ itemCode: string | null }>;
}

export interface PresetFile {
  generatedAt: string;
  source: { from: string; expectBasis: string; age: string; weight: string };
  presets: Preset[];
}

/** 형식 검사 — 통과면 null, 실패면 사유 문자열(AC-63). */
export const validatePreset = (p: unknown): string | null => {
  if (!p || typeof p !== "object") return "preset가 객체가 아님";
  const preset = p as Record<string, unknown>;
  if (typeof preset.id !== "string") return "id 누락";
  if (typeof preset.name !== "string") return "name 누락";
  if (typeof preset.expect !== "string" || !ALLOWED_EXPECT.has(preset.expect))
    return `expect 값이 허용 밖: ${String(preset.expect)}`;
  const req = preset.request as Record<string, unknown> | undefined;
  if (!req || typeof req !== "object" || !Array.isArray(req.prescriptions))
    return "request.prescriptions 누락";
  if ("weightKg" in req) return "최상위 weightKg 금지(patient 안에 있어야 함)";
  if (!Array.isArray(preset.rows)) return "rows 누락";
  if (preset.rows.length !== (req.prescriptions as unknown[]).length)
    return "rows 길이 ≠ prescriptions 길이";
  return null;
};

/** 형식 검사를 통과한 프리셋만 반환. */
export const loadPresets = (file: unknown): Preset[] => {
  const f = file as { presets?: unknown } | null;
  if (!f || !Array.isArray(f.presets)) return [];
  return (f.presets as unknown[]).filter((p) => validatePreset(p) === null) as Preset[];
};

export const PRESET_FILE = presetFileJson as unknown as PresetFile;
export const PRESET_GENERATED_AT: string = PRESET_FILE.generatedAt;
export const defaultPresets: Preset[] = loadPresets(PRESET_FILE);
